import { isGeojsonPoint } from './isGeojsonPoint';
import { isGeojsonPosition } from './isGeojsonPosition';
import { isLatLngObject } from './isLatLngObject';
import { isLatitudeAndLongitudeObject } from './isLatitudeAndLongitudeObject';

/**
 * Converts GeoJSON coordinates or position to a lat/lng object
 *
 * GeoJSON coordinates follow the format [longitude, latitude] or [longitude, latitude, altitude].
 * This function extracts the coordinates and returns them in a {lat, lng} format.
 *
 * @param coordinate - GeoJSON position array [longitude, latitude],
 *                     or an object with lng/lat or longitude/latitude properties,
 *                     or a GeoJSON Point geometry object
 * @returns Object with lat and lng properties
 * @throws Error if the coordinate format is invalid
 *
 * @example
 * // From GeoJSON position array
 * getLatLng([10.5, 51.2]) // Returns { lat: 51.2, lng: 10.5 }
 *
 * // From object with lng/lat properties
 * getLatLng({ lng: 10.5, lat: 51.2 }) // Returns { lat: 51.2, lng: 10.5 }
 *
 * // From object with longitude/latitude properties
 * getLatLng({ longitude: 10.5, latitude: 51.2 }) // Returns { lat: 51.2, lng: 10.5 }
 *
 * // From GeoJSON Point geometry
 * getLatLng({ type: 'Point', coordinates: [10.5, 51.2] }) // Returns { lat: 51.2, lng: 10.5 }
 */
export function getLatLng(
  coordinate:
    | Array<number>
    | { lng: number; lat: number }
    | { longitude: number; latitude: number }
    | { type: 'Point'; coordinates: Array<number> },
): { lat: number; lng: number } {
  // Handle GeoJSON Point geometry
  if (isGeojsonPoint(coordinate)) {
    const { coordinates } = coordinate;
    if (coordinates.length < 2) {
      throw new Error(
        'Invalid GeoJSON Point. Coordinates must contain at least 2 values [longitude, latitude]',
      );
    }

    const [lng, lat] = coordinates;
    return {
      lat,
      lng,
    };
  }

  // Handle array type [longitude, latitude]
  if (isGeojsonPosition(coordinate)) {
    return {
      lat: coordinate[1],
      lng: coordinate[0],
    };
  }

  // Handle object with lng/lat properties
  if (isLatLngObject(coordinate)) {
    return {
      lat: coordinate.lat,
      lng: coordinate.lng,
    };
  }

  // Handle object with longitude/latitude properties
  if (isLatitudeAndLongitudeObject(coordinate)) {
    return {
      lat: coordinate.latitude,
      lng: coordinate.longitude,
    };
  }

  throw new Error(
    'Invalid coordinate format. Must be an array [longitude, latitude], an object with lng/lat or longitude/latitude properties, or a GeoJSON Point',
  );
}
